import React, { useState } from 'react';
import { API, API_BASE_URL } from '../../config/api';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Clock, Loader2, Image, Plus, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

export const GalleryTab = ({ galleryItems, adminKey, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newItem, setNewItem] = useState({
    title: '', category: 'Decorating', description: '',
    beforeImage: '', afterImage: '', duration: '', savings: ''
  });

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNewItem(prev => ({ ...prev, [field]: response.data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleAdd = async () => {
    if (!newItem.title || !newItem.beforeImage || !newItem.afterImage) {
      toast.error('Please fill in title and upload both images');
      return;
    }

    try {
      setUploading(true);
      await axios.post(`${API}/admin/gallery?admin_key=${adminKey}`, newItem);
      toast.success('Gallery item added');
      setNewItem({
        title: '', category: 'Decorating', description: '',
        beforeImage: '', afterImage: '', duration: '', savings: ''
      });
      setShowForm(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to add gallery item');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this gallery item?')) return;

    try {
      await axios.delete(`${API}/admin/gallery/${itemId}?admin_key=${adminKey}`);
      toast.success('Gallery item deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete gallery item');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg font-['Outfit']">Before/After Gallery</h3>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="btn-accent text-sm"
          data-testid="add-gallery-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Project
        </Button>
      </div>

      {/* Add Form */}
      {showForm && (
        <Card className="border-primary/50">
          <CardContent className="p-4 space-y-4">
            <h4 className="font-semibold text-sm">Add New Gallery Project</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Project Title *</Label>
                <Input
                  value={newItem.title}
                  onChange={(e) => setNewItem(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Living Room Repaint"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <select
                  value={newItem.category}
                  onChange={(e) => setNewItem(prev => ({ ...prev, category: e.target.value }))}
                  className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="Decorating">Decorating</option>
                  <option value="Carpentry">Carpentry</option>
                  <option value="Outdoors">Outdoors</option>
                  <option value="Plumbing">Plumbing</option>
                  <option value="General">General</option>
                  <option value="Electrics">Electrics</option>
                </select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Description</Label>
              <Textarea
                value={newItem.description}
                onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the work done..."
                className="mt-1 text-sm"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Before Image *</Label>
                <div className="mt-1 border-2 border-dashed border-border rounded-lg p-4 text-center">
                  {newItem.beforeImage ? (
                    <div className="relative">
                      <img
                        src={`${API_BASE_URL}${newItem.beforeImage}`}
                        alt="Before"
                        className="w-full h-32 object-cover rounded"
                      />
                      <button
                        onClick={() => setNewItem(prev => ({ ...prev, beforeImage: '' }))}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <span className="text-xs text-muted-foreground">Upload Before</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'beforeImage')}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs">After Image *</Label>
                <div className="mt-1 border-2 border-dashed border-border rounded-lg p-4 text-center">
                  {newItem.afterImage ? (
                    <div className="relative">
                      <img
                        src={`${API_BASE_URL}${newItem.afterImage}`}
                        alt="After"
                        className="w-full h-32 object-cover rounded"
                      />
                      <button
                        onClick={() => setNewItem(prev => ({ ...prev, afterImage: '' }))}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <span className="text-xs text-muted-foreground">Upload After</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'afterImage')}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Duration</Label>
                <Input
                  value={newItem.duration}
                  onChange={(e) => setNewItem(prev => ({ ...prev, duration: e.target.value }))}
                  placeholder="e.g., 2 days"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Customer Savings</Label>
                <Input
                  value={newItem.savings}
                  onChange={(e) => setNewItem(prev => ({ ...prev, savings: e.target.value }))}
                  placeholder="e.g., £180"
                  className="mt-1 text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="btn-accent"
                onClick={handleAdd}
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                Add Project
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gallery Grid */}
      {galleryItems.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Image className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-semibold mb-2 font-['Outfit']">No gallery items yet</h3>
            <p className="text-muted-foreground mb-4">Add before/after photos of your completed work</p>
            <Button onClick={() => setShowForm(true)} className="btn-accent">
              <Plus className="w-4 h-4 mr-1" /> Add First Project
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {galleryItems.map((item) => {
            const baseUrl = API_BASE_URL || '';
            const beforeUrl = item.beforeImage?.startsWith('http') ? item.beforeImage : `${baseUrl}${item.beforeImage?.startsWith('/api') ? item.beforeImage : `/api${item.beforeImage}`}`;
            const afterUrl = item.afterImage?.startsWith('http') ? item.afterImage : `${baseUrl}${item.afterImage?.startsWith('/api') ? item.afterImage : `/api${item.afterImage}`}`;

            return (
              <Card key={item.id} className="overflow-hidden group">
                <div className="relative">
                  <div className="grid grid-cols-2 gap-0.5">
                    <img
                      src={beforeUrl}
                      alt="Before"
                      className="w-full h-32 object-cover bg-muted"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">Before</text></svg>';
                      }}
                    />
                    <img
                      src={afterUrl}
                      alt="After"
                      className="w-full h-32 object-cover bg-muted"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">After</text></svg>';
                      }}
                    />
                  </div>
                  <div className="absolute top-2 left-2">
                    <Badge className="text-xs bg-black/70 text-white">{item.category}</Badge>
                  </div>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                <CardContent className="p-3">
                  <h4 className="font-semibold text-sm truncate">{item.title}</h4>
                  <p className="text-xs text-muted-foreground line-clamp-2">{item.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    {item.duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{item.duration}</span>}
                    {item.savings && <span className="text-green-600 font-medium">Saved {item.savings}</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
